<template>
  <div class="grid w-full">
    <div class="col-start-1 row-start-1 flex min-w-0 items-center">
      <div v-if="$slots.left" class="flex-shrink-0">
        <slot name="left"></slot>
      </div>

      <div ref="scrollableContainer" class="hide-scrollbar min-w-0 flex-1 overflow-x-auto scroll-smooth">
        <slot></slot>
      </div>

      <div v-if="$slots.right" class="flex-shrink-0">
        <slot name="right"></slot>
      </div>
    </div>

    <div class="pointer-events-none col-start-1 row-start-1 flex items-center justify-between">
      <IAIScrollableContainerButton
        v-if="showArrows"
        position="left"
        :can-scroll="canScrollLeft"
        :variant="variant"
        :transformX="buttonTransformX"
        :transformY="buttonTransformY"
        @scroll="scrollLeft"
      />

      <IAIScrollableContainerButton
        v-if="showArrows"
        position="right"
        :can-scroll="canScrollRight"
        :variant="variant"
        :transformX="buttonTransformX"
        :transformY="buttonTransformY"
        @scroll="scrollRight"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from "vue";
import { useMagicKeys, useResizeObserver, useEventListener, useDebounceFn, useMutationObserver } from "@vueuse/core";
import IAIScrollableContainerButton from "./IAIScrollableContainerButton.vue";

interface Props {
  scrollDistance?: number;
  showArrows?: boolean;
  enableKeyboardNavigation?: boolean;
  variant?: "primary" | "secondary";
  buttonTransformX?: number;
  buttonTransformY?: number;
}

const props = withDefaults(defineProps<Props>(), {
  scrollDistance: 200,
  showArrows: true,
  enableKeyboardNavigation: true,
  variant: "primary",
  buttonTransformX: 0,
  buttonTransformY: 0,
});

const scrollableContainer = ref<HTMLElement | null>(null);
const scrollPosition = ref(0);
const containerWidth = ref(0);
const scrollWidth = ref(0);

const updateScrollMetrics = () => {
  if (!scrollableContainer.value) return;
  scrollPosition.value = scrollableContainer.value.scrollLeft;
  containerWidth.value = scrollableContainer.value.offsetWidth;
  scrollWidth.value = scrollableContainer.value.scrollWidth;
};

const debouncedUpdateMetrics = useDebounceFn(updateScrollMetrics, 150);

useEventListener(scrollableContainer, "scroll", () => debouncedUpdateMetrics());
useResizeObserver(scrollableContainer, debouncedUpdateMetrics);
useMutationObserver(scrollableContainer, debouncedUpdateMetrics, {
  childList: true,
  subtree: true,
  attributes: true,
});

onMounted(() => {
  updateScrollMetrics();
});

const scrollLeft = () => {
  if (!scrollableContainer.value || !canScrollLeft.value) return;
  scrollableContainer.value.scrollBy({ left: -props.scrollDistance, behavior: "smooth" });
};

const scrollRight = () => {
  if (!scrollableContainer.value || !canScrollRight.value) return;
  scrollableContainer.value.scrollBy({ left: props.scrollDistance, behavior: "smooth" });
};

const canScrollLeft = computed(() => scrollPosition.value > 0);
const canScrollRight = computed(() => scrollPosition.value + containerWidth.value < scrollWidth.value - 1);

if (props.enableKeyboardNavigation) {
  useMagicKeys({
    onEventFired: (e: KeyboardEvent) => {
      if (e.type !== "keydown") return;

      if (e.key === "ArrowLeft") {
        scrollLeft();
      } else if (e.key === "ArrowRight") {
        scrollRight();
      }
    },
  });
}
</script>

<style scoped lang="scss">
.hide-scrollbar {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari and Opera */
  }
}
</style>
